import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../services/api';

const AuthContext = createContext(null);

// Logout automático após 15 minutos de inatividade (apenas cliques)
const IDLE_TIMEOUT_MS = 15 * 60 * 1000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const idleTimerRef = useRef(null);

  useEffect(() => {
    const initAuth = async () => {
      try {
        const response = await api.get('/auth/me');
        setUser(response.data);
      } catch (error) {
        setUser(null);
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    if (response.data.mfa_required) {
      return { mfaRequired: true, mfaToken: response.data.mfa_token };
    }
    const { user: userData } = response.data;
    setUser(userData);
    return { mfaRequired: false, user: userData };
  };

  const verifyMfa = async (mfaToken, code) => {
    const response = await api.post('/auth/mfa/verify', { mfa_token: mfaToken, code });
    setUser(response.data.user);
    return response.data.user;
  };

  const register = async (name, email, password, role = 'usuario') => {
    const response = await api.post('/auth/register', { name, email, password, role });
    const { user: userData } = response.data;
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (e) {
      // cookies limpos server-side; ignora falha de rede
    }
    setUser(null);
  };

  // Logout automático por inatividade (somente cliques reiniciam o contador)
  const idleLogout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch (e) {
      // ignora falha de rede
    }
    setUser(null);
    toast.warning('Sessão encerrada por inatividade. Faça login novamente.');
  }, []);

  useEffect(() => {
    if (!user) return undefined;

    const resetTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(idleLogout, IDLE_TIMEOUT_MS);
    };

    resetTimer();
    window.addEventListener('click', resetTimer);

    return () => {
      window.removeEventListener('click', resetTimer);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [user, idleLogout]);

  const refreshUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
      return response.data;
    } catch (err) {
      return null;
    }
  };

  const isAdmin = () => user?.role === 'admin';
  const isGestor = () => user?.role === 'gestor' || user?.role === 'admin';
  const hasPermission = (module, action) => {
    if (!user?.permissions) return false;
    return user.permissions[module]?.[action] === true;
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      verifyMfa,
      register,
      logout,
      isAdmin,
      isGestor,
      hasPermission,
      refreshUser,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
